/**
 * @Description 执行审批-通过弹框
 */
import { FC, useEffect, useMemo } from 'react';
import { Form, Modal } from 'antd';
import { useMethods } from '@lhb/hook';
import V2Form from '@/common/components/Form/V2Form';
import V2FormTextArea from '@/common/components/Form/V2FormTextArea/V2FormTextArea';
import V2FormUpload from '@/common/components/Form/V2FormUpload/V2FormUpload';
import { ApprovePageNo } from '@/common/components/business/ExpandStore/ts-config';
import { isArray, isEqual, isNotEmptyAny } from '@lhb/func';
import { dealApprovalSumitExtras } from '@/common/components/business/ExpandStore/ApprovalSelectNodes/config';
import ApprovalSelectNodes from '@/common/components/business/ExpandStore/ApprovalSelectNodes';
import styles from './index.module.less';

interface PassDialogProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  aprDetail?: any;
  onSubmit?: (reason: string, extraParams: any) => void; // 提交
}

const PassDialog: FC<PassDialogProps> = ({
  visible,
  setVisible,
  aprDetail,
  onSubmit,
}) => {

  const [form] = Form.useForm();

  /**
   * @description 上传文件组件设置的解析
   * @return
   *  未找到null
   *  有值时格式
   *  {
        "value": 2,
        "required": false
      }
   */
  const uploadFieldAccess = useMemo(() => {
    if (!isNotEmptyAny(aprDetail)) return null;

    let access: any = null;
    const { pageNo, nodeCode, nodes } = aprDetail;
    if (pageNo && nodeCode && isArray(nodes)) {
      const targetNode = nodes.find((itm: any) => itm.nodeCode === nodeCode);
      if (targetNode) {
        const { fields } = targetNode;
        if (isArray(fields)) {
          const targetField = fields.find((itm: any) => itm.field === pageNo);
          if (targetField) {
            access = targetField.access;
          }
        }
      }
    }
    return access;
  }, [aprDetail]);

  const isUploadRequired = useMemo(() => {
    return isNotEmptyAny(uploadFieldAccess) && !!uploadFieldAccess?.required;
  }, [uploadFieldAccess]);

  const isUploadVisible = useMemo(() => {
    // 1：仅可见  2：可写 3:不可见
    return isNotEmptyAny(uploadFieldAccess) && [1, 2].includes(uploadFieldAccess?.value);
  }, [uploadFieldAccess]);

  const isUploadDisabled = useMemo(() => {
    // 1：仅可见  2：可写 3:不可见
    return isNotEmptyAny(uploadFieldAccess) && uploadFieldAccess?.value === 1;
  }, [uploadFieldAccess]);

  /**
   * @description 是否需要展示上传文件，是点位评估对应结点+通过审批时需要上传
   */
  const showUploadFile = useMemo(() => {
    const { pageNo } = aprDetail || {};
    return pageNo === ApprovePageNo.EVALUATION001 && isUploadVisible;
  }, [aprDetail, isUploadVisible]);

  const targetCusFields = useMemo(() => {
    const { cusFields } = aprDetail || {};
    if (isArray(cusFields) && cusFields.length && cusFields[0].name) {
      return cusFields[0]; // 写死取第一项
    }
    return null;
  }, [aprDetail]);

  /**
   * @description 弹框显示且有详情时，文件回显
   */
  useEffect(() => {
    if (!visible || !isNotEmptyAny(aprDetail)) return;

    const { evaluationProcessInfo } = aprDetail;
    const attachVal = isArray(evaluationProcessInfo?.attachments) && evaluationProcessInfo.attachments.length ? evaluationProcessInfo.attachments : [];
    form.setFieldValue('attachments', attachVal);
  }, [visible]);

  const methods = useMethods({
    handleCancel() {
      form.resetFields();
      setVisible(false);
    },
    handleOk() {
      form.validateFields().then(async (values: any) => {
        let extraParams: any = {};
        let { attachments } = values;

        if (showUploadFile && isArray(attachments) && attachments.length) {
          if (this.attachmentsHasChanged(attachments)) {
            attachments = attachments.map((itm: any) => {
              const { name, url, type } = itm;
              return { name, url, type };
            });
            extraParams = {
              evaluationProcessInfo: { attachments }
            };
          }
        }

        // 选节点选审批人
        const selectNodeExtras: any = dealApprovalSumitExtras(values);
        extraParams = {
          ...extraParams,
          ...selectNodeExtras,
        };
        setVisible(false);
        const { cusFields } = values;
        if (cusFields) { // 如果显示了自定义字段
          extraParams.cusFields = [{
            name: targetCusFields.name,
            value: cusFields
          }];
        }
        onSubmit && onSubmit(values.reason, extraParams);
      });
    },
    /**
     * @description 检查上传附件是否变动过。
     *   没有变动的话提交时不带该参数，防止出现未修改附件但后台的文件上传者却被更新
     */
    attachmentsHasChanged(curAttachments: any) {
      if (!isNotEmptyAny(aprDetail)) return false;

      const { evaluationProcessInfo } = aprDetail;
      const oldVal = isArray(evaluationProcessInfo?.attachments) && evaluationProcessInfo.attachments.length ? evaluationProcessInfo.attachments.map((itm: any) => itm.url) : [];

      const newVal = curAttachments.map((itm: any) => itm.url);
      const res = !isEqual(oldVal, newVal);
      return res;
    }
  });

  return (
    <Modal
      title='审批意见'
      open={visible}
      destroyOnClose={true}
      width={showUploadFile ? 480 : 336}
      onCancel={methods.handleCancel}
      onOk={methods.handleOk}
      className={styles.passDialog}
    >
      <V2Form form={form}>
        <ApprovalSelectNodes
          aprDetail={aprDetail}
        />
        {showUploadFile ? <V2FormUpload
          label='上传附件'
          name='attachments'
          uploadType='file'
          config={{
            size: 30,
            maxCount: 5,
            fileType: 'any',
          }}
          rules={[{ required: isUploadRequired, message: '请上传附件' }]}
          disabled={isUploadDisabled}
        /> : <></>}
        {
          targetCusFields && targetCusFields.name ? <V2FormTextArea
            label={targetCusFields.name}
            name='cusFields'
            maxLength={200}
            placeholder={targetCusFields.placeHolder || `请输入${targetCusFields.name}`}
            rules={[{ required: true, message: `请输入${targetCusFields.name}` }]}
            config={{
              showCount: true,
            }}
          /> : <></>
        }
        <V2FormTextArea
          label='输入审批意见'
          name='reason'
          maxLength={500}
          placeholder='请输入意见，最多输入500字'
          rules={[{ required: true, message: '请输入意见' }]}
          config={{
            showCount: true,
          }}
        />
      </V2Form>
    </Modal>
  );
};
export default PassDialog;
